/**
 * CharacterSummary — Sprint 17g-4 Task 5.7
 *
 * Displays the LLM-generated character profile: a headline plus
 * per-category narrative cards with accuracy-driven specificity.
 * Includes a regenerate button to trigger fresh LLM generation.
 */

import { cn } from '@ownyou/ui-design-system';
import type { CharacterProfile } from '../../services/character-summary';

export interface CharacterSummaryProps {
  profile: CharacterProfile;
  onRegenerate?: () => void;
  isGenerating?: boolean;
}

export function CharacterSummary({ profile, onRegenerate, isGenerating }: CharacterSummaryProps) {
  return (
    <div data-testid="character-summary" className="space-y-4">
      {/* Headline */}
      <div className="flex items-start justify-between gap-2">
        <p className="text-lg font-serif text-neutral-800 leading-relaxed">
          {profile.headline}
        </p>
        {onRegenerate && (
          <button
            data-testid="character-summary-regenerate"
            onClick={onRegenerate}
            disabled={isGenerating}
            className={cn(
              'shrink-0 text-xs px-2 py-1 rounded-md border transition-colors',
              isGenerating
                ? 'border-neutral-200 text-neutral-400 cursor-not-allowed'
                : 'border-neutral-300 text-neutral-500 hover:text-neutral-700 hover:border-neutral-400',
            )}
          >
            {isGenerating ? 'Generating...' : 'Refresh'}
          </button>
        )}
      </div>

      {/* Per-category summaries */}
      {profile.categories.length === 0 && (
        <p className="text-sm text-neutral-400 italic">
          No category insights yet. Keep interacting to build your profile.
        </p>
      )}
      {profile.categories.map(cat => (
        <div
          key={cat.category}
          data-testid={`character-category-${cat.category}`}
          className="pl-3 border-l-2 border-neutral-100"
        >
          <div className="flex items-baseline justify-between">
            <p className="text-xs text-neutral-400 uppercase tracking-wide">{cat.category}</p>
            {cat.accuracy > 0 && (
              <span className="text-[10px] text-neutral-300">
                {(cat.accuracy * 100).toFixed(0)}% accuracy
              </span>
            )}
          </div>
          <p className="text-sm text-neutral-700 mt-0.5 leading-relaxed">{cat.summary}</p>
        </div>
      ))}

      {/* Last updated timestamp */}
      {profile.lastUpdated > 0 && (
        <p className="text-[10px] text-neutral-300 text-right">
          Updated {new Date(profile.lastUpdated).toLocaleDateString()}
        </p>
      )}
    </div>
  );
}
